import "./sidebarSpecimensForm.css";

import { DocumentDownloadIcon, PlusIcon } from "@heroicons/react/outline";
import classNames from "classnames";
import { useState } from "react";

import {
  Accession,
  CaseAdmin,
  CaseDocumentAdmin,
  SpecimenProtocolOverride,
  Specimen,
} from "pwapi";

import { AccessionSpecimen } from "custom-models";

import { getProtocolStatus, protocolLabels } from "shared/utils/specimenUtils";

import { useAppSelector } from "app/hooks";
import { openDocument } from "app/reducers/admin/actions/caseActions";
import { useSelectedLab } from "hooks/useSelectedLabStation";
import Sidebar from "ui/components/sidebar";
import { specimenSort } from "util/sortFunctions";

import SidebarDocumentUploadForm from "./sidebarDocumentUploadForm";
import SidebarSpecimensForm from "./sidebarSpecimensForm";
import SpecimenListItem from "./SpecimenListItem";

type AccessionSidebarProps = {
  specimens: Accession["specimens"];
  documents?: CaseAdmin["documents"];
  case_id?: number;
  onSave: Function;
  onDelete: Function;
  hideSubmitButton?: boolean;
  caseType?: Accession.case_type;
};

const AccessionSidebar = ({
  specimens,
  documents,
  case_id,
  onSave,
  onDelete,
  caseType,
}: AccessionSidebarProps) => {
  const [editingSpecimen, setEditingSpecimen] = useState<{
    specimen: AccessionSpecimen | null;
    index: number | undefined;
  }>({ specimen: null, index: undefined });

  const difEnabled = useAppSelector(
    (state) => !!state.sys.features.switches?.difProtocol
  );

  const [isDocumentFormOpen, setIsDocumentFormOpen] = useState<boolean>(false);

  const selectedLab = useSelectedLab();

  const pashReflexEnabled = selectedLab?.settings?.pash_reflex?.active;
  const pasfReflexEnabled = selectedLab?.settings?.pasf_reflex?.active;

  const hasDifProtocol = specimens?.some(
    (s: AccessionSpecimen) =>
      difEnabled &&
      getProtocolStatus(s, SpecimenProtocolOverride.protocol.DIF).active
  );
  const hasNailProtocol = specimens?.some(
    (s: AccessionSpecimen) =>
      getProtocolStatus(s, SpecimenProtocolOverride.protocol.NAIL).active
  );
  const hasAlopeciaProtocol = specimens?.some(
    (s: AccessionSpecimen) =>
      getProtocolStatus(
        s,
        SpecimenProtocolOverride.protocol.ALOPECIA,
        undefined,
        selectedLab.settings
      ).active
  );
  const hasPashReflexProtocol = specimens?.some(
    (s: AccessionSpecimen) =>
      pashReflexEnabled &&
      getProtocolStatus(
        s,
        SpecimenProtocolOverride.protocol.PASH_REFLEX,
        undefined,
        selectedLab.settings
      ).active
  );
  const hasPasfReflexProtocol = specimens?.some(
    (s: AccessionSpecimen) =>
      pasfReflexEnabled &&
      getProtocolStatus(
        s,
        SpecimenProtocolOverride.protocol.PASF_REFLEX,
        undefined,
        selectedLab.settings
      ).active
  );

  const pasfLabStain = useAppSelector((state) =>
    Object.values(state.labStains).find(
      (ls) =>
        ls.lab_id === selectedLab.id &&
        state.stains[ls.stain_id]?.abbreviation === "PAS-F"
    )
  );

  const pashLabStain = useAppSelector((state) =>
    Object.values(state.labStains).find(
      (ls) =>
        ls.lab_id === selectedLab.id &&
        state.stains[ls.stain_id]?.abbreviation === "PAS-H"
    )
  );

  const difLabStain = useAppSelector((state) =>
    Object.values(state.labStains).find(
      (ls) =>
        ls.lab_id === selectedLab.id &&
        state.stains[ls.stain_id]?.name === "DIF"
    )
  );

  const alopeciaPanel = useAppSelector((state) =>
    Object.values(state.labPanels).find(
      (lp) =>
        lp.lab_id === selectedLab.id && lp.name.toLowerCase() === "alopecia"
    )
  );

  return (
    <Sidebar
      className={classNames({
        "Sidebar--documentFormOpen":
          editingSpecimen.specimen || isDocumentFormOpen,
      })}
    >
      {!editingSpecimen.specimen && !isDocumentFormOpen && (
        <>
          <div className="specimens__action">
            <span>Specimen</span>
          </div>
          <div className="specimens__list">
            {[...(specimens || [])]
              .sort(specimenSort)
              .map((specimen: any, index: number) => {
                return (
                  <SpecimenListItem
                    specimen={specimen}
                    specimenIndex={index}
                    setEditingSpecimen={setEditingSpecimen}
                    handleOnDelete={onDelete}
                    key={`${index}-${specimen.name}-${specimen.type}-${specimen.procedure}-${specimen.site}`}
                    lastSpecimen={index === specimens.length - 1}
                    isCaseAccessioned={!!case_id}
                    specimens={specimens}
                    caseType={caseType}
                  />
                );
              })}
            <button
              className="add-specimen-button Button Button--purple"
              onClick={() => {
                setEditingSpecimen({
                  specimen: {
                    name: String.fromCharCode(specimens.length + 65),
                    lis_state: Specimen.lis_state.BOTTLED,
                    protocol_overrides: [],
                  },
                  index: specimens.length,
                });
              }}
            >
              Add Specimen <PlusIcon />
            </button>

            {hasNailProtocol && !pasfLabStain && !pashReflexEnabled && (
              <div className="red">
                <em>
                  <small>
                    NAIL Protocol should order a PAS-F Stain by default, but
                    none could be found for {selectedLab.name}
                  </small>
                </em>
              </div>
            )}

            {hasNailProtocol && !pashLabStain && pashReflexEnabled && (
              <div className="red">
                <em>
                  <small>
                    NAIL Protocol should order a PAS-H Stain by default, but
                    none could be found for {selectedLab.name}
                  </small>
                </em>
              </div>
            )}

            {hasPashReflexProtocol && !pashLabStain && (
              <div className="red">
                <em>
                  <small>
                    {protocolLabels.pash_reflex} Protocol should order a PAS-H
                    Stain by default, but none could be found for{" "}
                    {selectedLab.name}
                  </small>
                </em>
              </div>
            )}

            {hasPasfReflexProtocol && !pasfLabStain && (
              <div className="red">
                <em>
                  <small>
                    {protocolLabels.pasf_reflex} Protocol should order a PAS-F
                    Stain by default, but none could be found for{" "}
                    {selectedLab.name}
                  </small>
                </em>
              </div>
            )}

            {hasAlopeciaProtocol && !alopeciaPanel && (
              <div className="red">
                <em>
                  <small>
                    ALOPECIA Protocol should order an Alopecia Panel by default,
                    but none could be found for {selectedLab.name}
                  </small>
                </em>
              </div>
            )}

            {hasDifProtocol && !difLabStain && (
              <div className="red">
                <em>
                  <small>
                    DIF Protocol is not configured for {selectedLab.name}
                  </small>
                </em>
              </div>
            )}
          </div>

          {case_id && (
            <div className="documents__list">
              <div className="specimens__action">
                <span>Documents</span>
              </div>
              {documents &&
                documents.map((doc, index) => (
                  <div
                    key={`${doc.id}-${index}`}
                    className={classNames(
                      "documents__listItem",
                      `documents__listItem--${doc.status}`
                    )}
                    onClick={() => {
                      doc.id &&
                        doc.status === CaseDocumentAdmin.status.UPLOADED &&
                        openDocument(doc.id);
                    }}
                  >
                    {doc.name}{" "}
                    {doc.status === CaseDocumentAdmin.status.UPLOADED ? (
                      <DocumentDownloadIcon height={20} />
                    ) : (
                      "(pending)"
                    )}
                  </div>
                ))}
              <button
                type="button"
                className="Button Button--blue"
                style={{ width: "100%" }}
                onClick={() => setIsDocumentFormOpen(true)}
              >
                Upload Document <PlusIcon />
              </button>
            </div>
          )}
        </>
      )}
      {editingSpecimen.specimen && (
        <SidebarSpecimensForm
          specimen={editingSpecimen.specimen}
          onSubmit={(
            specimen: AccessionSpecimen,
            slowMohsSegmentCount?: number
          ) => {
            setEditingSpecimen({ specimen: null, index: undefined });
            onSave(specimen, editingSpecimen.index, slowMohsSegmentCount);
          }}
          onClose={() => {
            setEditingSpecimen({ specimen: null, index: undefined });
          }}
          isCaseAccessioned={!!case_id}
          specimens={specimens}
          caseType={caseType}
        />
      )}
      {isDocumentFormOpen && case_id && (
        <SidebarDocumentUploadForm
          onClose={() => setIsDocumentFormOpen(false)}
          case_id={case_id}
        />
      )}
    </Sidebar>
  );
};

export default AccessionSidebar;
